import React from 'react';
import styled, { keyframes } from 'styled-components';
import SectionTitle from '@flow/scenes/home/components/section-title/SectionTitle';
import em from 'polished/lib/helpers/em';
import { Link } from 'react-router-dom';
import BasicButton from '@flow/components/ui/button/BasicButton';
import {getRelativeUrl} from '@flow/utils/cleaners';

const slideForward = keyframes`
  0%,
  100% {
    transform: translate(0, 0);
  }

  50% {
    transform: translate(10px, 0);
  }
`;

const Wrapper = styled.div`
  background-color: ${props => props.theme.highlightColor};
  padding-bottom: 10em;
  
  @media (max-width: 1000px) {
    padding-bottom: 3em;
  }
`;

const TicketsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 0 10em;
  
  @media (max-width: 1920px) {
    padding: 0 ${100 / (1920 * 0.01)}vw;
  }
  
  @media (max-width: 1250px) {
    padding: 0 3em;
  }
  
  @media (max-width: 1000px) {
    display: none;
  }
`;

const TicketContent = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column; 
  justify-content: space-between;
`;

const TicketTypeHeader = styled.header`
  .dingbats {
    font-size: 4.8em;
    letter-spacing: ${em(1, 8.8)};
    
    @media (max-width: 1920px) {
      font-size: ${48 / (1920 * 0.01)}vw;
    }
  }
`;

const TicketTypeFooter = styled.footer`
  font-size: 2.1em;
  min-height: 150px;
  box-sizing: border-box;
  text-transform: uppercase;
  line-height: ${em(3.2, 2.1)};
  border-top: ${em(0.1, 2.1)} solid black;
  padding-top: ${em(3, 2.1)};
  margin-top: ${em(4.4, 2.1)};
  
  span:after {
    font-family: Flow-Poster-Bold;
    content: '→';
    display: inline-block;
    transform-origin: center;
    margin-left: ${em(1.6, 2.1)};
  }
`;

const TicketType = styled.div`
  width: 30%;
  text-align: center;
  a {
    height: 100%;
    display: flex;
    background-color: white;
    box-sizing: border-box;
    padding: 3.6em 3em 2em;  
    
    &:${props => props.theme.hoverProperty} {
      ${TicketTypeFooter} span {
        color: ${props => props.theme.highlightColor};
        
        &:after {
          animation: ${slideForward} 0.6s ease-in-out 1;
        }
      }
    }
  }
`;

const TicketTypeTitleWrapper = styled.div`
`;

const TicketTypeTitle = styled.h1`
  font-size: 7em;
  margin-top: ${em(5, 7)};
  font-family: Flow-Poster-Bold;
  text-transform: uppercase;
  
  @media (max-width: 1920px) {
    font-size: ${70 / (1920 * 0.01)}vw;
  }
`;

const TicketTypeDescription = styled.p`
  font-size: 2.1em;
  line-height: ${em(3.2, 2.1)};
  margin-top: ${em(7.4, 2.1)};
  
  @media (max-width: 1200px) {
    padding-left: 0;
    padding-right: 0;
  }
  
  @media (max-width: 1100px) {
    margin-top: ${74 / (1100 * 0.01)}vw;
    //font-size: ${21 / (1100 * 0.01)}vw;
  }
  
  @media (max-width: 1080px) {
    font-size: 1.8em;
  }
`;

const NarrowTicketContainer = styled.div`
  text-align: center; 
  padding: 0 3em;
  
  @media (min-width: 1000px) {
    display: none;
  }
`;

const NarrowTicketContent = styled.p`
  font-size: 1.8em;
  line-height: ${em(3.2, 1.8)};
`;

const CTAWrapper = styled.div`
  margin-top: 5em;
`;

interface TicketType {
  description?: string;
  title?: string;
  link?: string;
  link_text?: string;
  is_sold_out?: boolean;
}

interface Mobile {
  cta_label?: string;
  subtitle?: string;
  cta_link?: {
    target?: string;
    title?: string;
    url?: string;
  }
}

interface Props {
  appLanguage: IAppLanguage;
  tickets?: {
    title?: string;
    ticket_types?: Array<TicketType>;
    mobile?: Mobile;
  };
}

const TicketsLift = ({
  appLanguage,
  tickets = {},
}: Props) => {

  const {
    title,
    ticket_types,
    mobile
  } = tickets;

  const renderTicket = (ticket: TicketType, key: number) => {
    /*return (
      <Link to={getRelativeUrl(ticket.link.url)}>
        {renderTicketContent(ticket, key)}
      </Link>
    );*/

    if (appLanguage.code === 'ru') {
      return (
        <a href={ticket.link.url} target='_blank'>
          {renderTicketContent(ticket, key)}
        </a>
      )
    } else {
      return (
        <Link to={getRelativeUrl(ticket.link.url)}>
          {renderTicketContent(ticket, key)}
        </Link>
      )
    }
  };

  const renderTicketContent = (ticket: TicketType, key: number) => {
    return (
      <TicketContent>
        <div>
          {key === 0 && <TicketTypeHeader><span className='dingbats'>✸</span></TicketTypeHeader>}
          {key === 1 && <TicketTypeHeader><span className='dingbats'>❢</span></TicketTypeHeader>}
          {key === 2 && <TicketTypeHeader><span className='dingbats'>❇</span></TicketTypeHeader>}

          <TicketTypeTitleWrapper>
            <TicketTypeTitle>
              {ticket.title}
            </TicketTypeTitle>
          </TicketTypeTitleWrapper>

          <TicketTypeDescription>
            {ticket.description}
          </TicketTypeDescription>
        </div>

        <div>
          <TicketTypeFooter>
            <span>{ticket.link_text}</span>
          </TicketTypeFooter>
        </div>
      </TicketContent>
    )
  };

  return (
    <Wrapper>
      {Boolean(title) && <SectionTitle title={title}/>}
      <TicketsContainer>
        {ticket_types.map((ticket: TicketType, key: number) => (
          <TicketType key={key}>
            {renderTicket(ticket, key)}
          </TicketType>
        ))}
      </TicketsContainer>
      {mobile &&
        <NarrowTicketContainer>
          <NarrowTicketContent>{mobile.subtitle}</NarrowTicketContent>
          {Boolean(mobile.cta_label) && Boolean(mobile.cta_link) &&
            <CTAWrapper>
              <BasicButton href={mobile.cta_link.url} target={mobile.cta_link.target} label={mobile.cta_label} highlightColor={'#FFFFFF'}/>
            </CTAWrapper>
          }
        </NarrowTicketContainer>
      }
    </Wrapper>
  )
};

export default TicketsLift;



// WEBPACK FOOTER //
// /Users/ek1z/dev/flowfestival-com/src/scenes/home/components/tickets-lift/TicketsLift.tsx